/**
 * Dialog de configuração para node Alterar Fase CRM
 * Permite configurar estratégia de seleção do deal e fase destino
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo dialog de configuração CRM
 * RELEASE: 1.1.0 - 2025-01-15 - Padronização do layout e uso de VariablePickerCombobox
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel as SelectGroupLabel,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
// ScrollArea removido - usando scroll nativo para robustez (v1.1.0)
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ArrowRightLeft, Search, MapPin, CheckCircle, XCircle, Info, HelpCircle, Zap } from 'lucide-react';
import { FlowNode, CrmChangePhaseConfig } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';

interface ConfigureCrmChangePhaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: CrmChangePhaseConfig) => void;
}

/**
 * Labels para exibição do filtro de status
 */
const STATUS_OPTIONS = [
  { value: 'open', label: 'Em Aberto', description: 'Apenas negócios em andamento' },
  { value: 'won', label: 'Concluído (Ganho)', description: 'Apenas negócios ganhos' },
  { value: 'lost', label: 'Perdido', description: 'Apenas negócios perdidos' },
  { value: 'all', label: 'Todos os Status', description: 'Qualquer status de negócio' },
];

/**
 * Dialog para configurar alteração de fase CRM
 * Seleção de deal + Fase destino
 */
export function ConfigureCrmChangePhaseDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureCrmChangePhaseDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estado do formulário
  const existingConfig = node.data.config as CrmChangePhaseConfig | undefined;
  
  // Estado para dialog de ajuda
  const [showHelp, setShowHelp] = useState(false);
  
  // Seleção do deal - novo padrão: from_trigger (RELEASE 1.2.0)
  const [dealSelection, setDealSelection] = useState<'most_recent' | 'from_variable' | 'from_trigger'>(
    (existingConfig?.deal_selection as 'most_recent' | 'from_variable' | 'from_trigger') || 'from_trigger'
  );
  
  // Variável do deal (leitura) - com ID para integração com VariablePickerCombobox
  const [dealVariableId, setDealVariableId] = useState(existingConfig?.deal_variable_id || '');
  const [dealVariableName, setDealVariableName] = useState(existingConfig?.deal_variable_name || '');
  
  const [dealStatusFilter, setDealStatusFilter] = useState<'open' | 'won' | 'lost' | 'all'>(
    existingConfig?.deal_status_filter || 'open'
  );
  
  // Fase destino
  const [phaseId, setPhaseId] = useState(existingConfig?.phase_id || '');
  const [phaseName, setPhaseName] = useState(existingConfig?.phase_name || '');
  const [phaseColor, setPhaseColor] = useState(existingConfig?.phase_color || '');
  const [pipelineName, setPipelineName] = useState(existingConfig?.pipeline_name || '');
  const [isWon, setIsWon] = useState(existingConfig?.is_won || false);
  const [isLost, setIsLost] = useState(existingConfig?.is_lost || false);

  // Sincroniza estado do formulário quando o node muda ou dialog abre
  // Necessário porque useState só usa valor inicial na 1ª montagem
  useEffect(() => {
    if (!open) return;
    const cfg = node.data.config as CrmChangePhaseConfig | undefined;
    setDealSelection((cfg?.deal_selection as 'most_recent' | 'from_variable' | 'from_trigger') || 'from_trigger');
    setDealVariableId(cfg?.deal_variable_id || '');
    setDealVariableName(cfg?.deal_variable_name || '');
    setDealStatusFilter(cfg?.deal_status_filter || 'open');
    setPhaseId(cfg?.phase_id || '');
    setPhaseName(cfg?.phase_name || '');
    setPhaseColor(cfg?.phase_color || '');
    setPipelineName(cfg?.pipeline_name || '');
    setIsWon(cfg?.is_won || false);
    setIsLost(cfg?.is_lost || false);
  }, [open, node.id]);

  // Query: Todas as fases de todos os pipelines ativos (agrupadas)
  const { data: allPhases, isLoading: loadingPhases } = useQuery({
    queryKey: ['all-crm-phases-grouped', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select(`
          id,
          name,
          color,
          is_won,
          is_lost,
          sort_order,
          pipeline:crm_pipelines!inner(
            id,
            name,
            is_active,
            workspace:crm_workspaces!inner(
              id,
              name,
              tenant_id
            )
          )
        `)
        .eq('pipeline.workspace.tenant_id', tenantId)
        .eq('pipeline.is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      
      // Agrupa fases por pipeline
      const phasesTyped = data as Array<{
        id: string;
        name: string;
        color: string | null;
        is_won: boolean;
        is_lost: boolean;
        sort_order: number;
        pipeline: {
          id: string;
          name: string;
          is_active: boolean;
          workspace: {
            id: string;
            name: string;
            tenant_id: string;
          };
        };
      }>;
      
      // Agrupa por pipeline_id
      const grouped: Record<string, { 
        pipelineId: string; 
        pipelineName: string; 
        workspaceName: string;
        phases: typeof phasesTyped;
      }> = {};
      
      for (const phase of phasesTyped) {
        const pipelineId = phase.pipeline.id;
        if (!grouped[pipelineId]) {
          grouped[pipelineId] = {
            pipelineId,
            pipelineName: phase.pipeline.name,
            workspaceName: phase.pipeline.workspace.name,
            phases: [],
          };
        }
        grouped[pipelineId].phases.push(phase);
      }
      
      return Object.values(grouped);
    },
    enabled: !!tenantId && open,
  });

  // Handler: Seleção de fase
  const handlePhaseSelect = (id: string) => {
    // Encontra a fase selecionada
    for (const group of allPhases || []) {
      const phase = group.phases.find(p => p.id === id);
      if (phase) {
        setPhaseId(id);
        setPhaseName(phase.name);
        setPhaseColor(phase.color || '');
        setPipelineName(group.pipelineName);
        setIsWon(phase.is_won);
        setIsLost(phase.is_lost);
        break;
      }
    }
  };

  // Validação do formulário
  const isFormValid = () => {
    if (!phaseId) return false;
    if (dealSelection === 'from_variable' && !dealVariableName.trim()) return false;
    return true;
  };

  // Handler: Salvar configuração
  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const config: CrmChangePhaseConfig = {
      deal_selection: dealSelection,
      deal_variable_name: dealSelection === 'from_variable' ? dealVariableName.trim() : undefined,
      deal_variable_id: dealSelection === 'from_variable' ? dealVariableId : undefined,
      // deal_status_filter só se aplica ao modo most_recent
      deal_status_filter: dealSelection === 'most_recent' ? dealStatusFilter : 'open',
      phase_id: phaseId,
      phase_name: phaseName,
      phase_color: phaseColor || undefined,
      pipeline_name: pipelineName || undefined,
      is_won: isWon || undefined,
      is_lost: isLost || undefined,
    };

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* v1.1.0: Padrão robusto de scroll - altura fixa + scroll nativo */}
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-violet-600" />
                Configurar Alterar Fase
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Mova um negócio existente para outra fase do pipeline
            </DialogDescription>
          </DialogHeader>

          {/* Body com scroll nativo - compatível com wheel, trackpad e drag */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Campo ID do Node */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* Seção: Seleção do Deal */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <Search className="h-4 w-4" />
                  Seleção do Negócio
                </div>

                {/* Modo de seleção */}
                <div className="space-y-3">
                  <Label>Como encontrar o negócio? *</Label>
                <RadioGroup
                    value={dealSelection}
                    onValueChange={(v) => setDealSelection(v as 'most_recent' | 'from_variable' | 'from_trigger')}
                    className="space-y-2"
                  >
                    {/* RELEASE 1.2.0: Opção from_trigger - usa o deal que disparou o gatilho */}
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="from_trigger" id="from_trigger" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="from_trigger" className="font-medium cursor-pointer flex items-center gap-1.5">
                          <Zap className="h-3.5 w-3.5 text-violet-500" />
                          Do negócio no gatilho CRM
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Usa o negócio que ativou o gatilho CRM ou de Evento de Contrato (padrão recomendado)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="most_recent" id="most_recent" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="most_recent" className="font-medium cursor-pointer">
                          Deal mais recente do contato
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Busca automaticamente o negócio mais recente associado ao contato
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="from_variable" id="from_variable" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="from_variable" className="font-medium cursor-pointer">
                          ID do deal em variável
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Usa o ID do negócio armazenado em uma variável do fluxo
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Filtro de status (apenas para most_recent) */}
                {dealSelection === 'most_recent' && (
                  <div className="space-y-2 pl-4 border-l-2 border-violet-200 dark:border-violet-800">
                    <Label htmlFor="status-filter">Filtro de Status</Label>
                    <Select value={dealStatusFilter} onValueChange={(v) => setDealStatusFilter(v as typeof dealStatusFilter)}>
                      <SelectTrigger id="status-filter">
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex flex-col">
                              <span>{option.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Define qual status do negócio será considerado na busca
                    </p>
                  </div>
                )}

                {/* Variável do deal (apenas para from_variable) - usando VariablePickerCombobox */}
                {dealSelection === 'from_variable' && (
                  <div className="space-y-2 pl-4 border-l-2 border-violet-200 dark:border-violet-800">
                    <Label>Variável com ID do Deal *</Label>
                    <VariablePickerCombobox
                      tenantId={tenantId}
                      value={dealVariableId || dealVariableName}
                      onChange={(variable) => {
                        if (variable) {
                          setDealVariableId(variable.id);
                          // CORREÇÃO: Usar display_name para exportação correta de variáveis
                          // CORREÇÃO: Usar variable.name (lowercase) para compatibilidade com engine
                          setDealVariableName(variable.name);
                        } else {
                          setDealVariableId('');
                          setDealVariableName('');
                        }
                      }}
                      allowCreate={false}
                      allowClear={false}
                      placeholder="Selecione a variável com o ID do deal"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use "ID do Negócio" (gatilho CRM) ou variável do node "Criar Negócio"
                    </p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Seção: Fase Destino */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <MapPin className="h-4 w-4" />
                  Fase de Destino
                </div>

                {/* Seleção de fase */}
                <div className="space-y-2">
                  <Label htmlFor="phase">Nova Fase *</Label>
                  <Select value={phaseId} onValueChange={handlePhaseSelect} disabled={loadingPhases}>
                    <SelectTrigger id="phase">
                      <SelectValue placeholder={loadingPhases ? 'Carregando...' : 'Selecione a fase destino'} />
                    </SelectTrigger>
                    <SelectContent>
                      {allPhases?.map((group) => (
                        <SelectGroup key={group.pipelineId}>
                          <SelectGroupLabel className="text-xs text-muted-foreground px-2 py-1.5">
                            {group.workspaceName} → {group.pipelineName}
                          </SelectGroupLabel>
                          {/* Filtra fases de ganho - requerem preenchimento manual de campos obrigatórios */}
                          {group.phases
                            .filter((phase) => !phase.is_won)
                            .map((phase) => (
                              <SelectItem key={phase.id} value={phase.id}>
                                <div className="flex items-center gap-2">
                                  {phase.color && (
                                    <span 
                                      className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: phase.color }}
                                    />
                                  )}
                                  <span>{phase.name}</span>
                                  {phase.is_lost && (
                                    <XCircle className="h-3 w-3 text-red-500" />
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    A fase pode ser de qualquer pipeline dentro da mesma workspace
                  </p>
                </div>

                {/* Informação da fase selecionada */}
                {phaseId && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                    <div className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Fase selecionada:</span>
                    </div>
                    <div className="pl-6 text-sm">
                      <div className="flex items-center gap-2">
                        {phaseColor && (
                          <span 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: phaseColor }}
                          />
                        )}
                        <span className="font-medium">{phaseName}</span>
                        {isWon && <span className="text-green-600 text-xs">(Ganho)</span>}
                        {isLost && <span className="text-red-600 text-xs">(Perdido)</span>}
                      </div>
                      {pipelineName && (
                        <p className="text-muted-foreground text-xs">Pipeline: {pipelineName}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!isFormValid()}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuda */}
      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="crm_change_phase" 
      />
    </>
  );
}
